import { useCallback, useEffect, useMemo, useState } from 'react'

import { isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import { deleteInternshipNote, fetchInternshipNote, upsertInternshipNote } from '../internshipNote'
import { onError, onSuccess } from '../../apiHelper'
import { getInternshipNotes, Internship } from '../../entities/internship'
import { Actions } from '../../events'
import { firstOrDefault, getIdOrNull, isObject } from '../..'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'

/**
 * The API only serializes the note owned by the caller's own organisation, and at most one of them,
 * so a single string is all there is to hold client side.
 *
 * Views asking for the NOTES serialization context already carry it on the internship ; the others
 * have to reload() it once they need it.
 *
 * @param {object} internship
 * @param {{ callback: function }} options called with the updated internship on every successful write
 */
const useInternshipNote = (internship, { callback } = {}) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)

  const safeCallback = useMemo(() => isFunction(callback) ? callback : () => { }, [callback])

  const internshipId = getIdOrNull(internship)
  const serializedNote = firstOrDefault(getInternshipNotes(internship))?.note ?? ''

  /**
   * Depends on the values and not on the internship itself : a parent rebuilding the object without
   * the NOTES context would otherwise wipe whatever reload() just fetched.
   */
  useEffect(() => setNote(serializedNote), [internshipId, serializedNote])

  const notify = useCallback((notes) => {
    safeCallback(new Internship({ ...internship, notes }), Actions.UPDATE)
  }, [internship, safeCallback])

  const reload = useCallback(() => {
    return fetchInternshipNote(user, internship).then(({ data }) => {
      const loaded = firstOrDefault(data)?.note ?? ''

      setNote(loaded)

      return loaded
    }).catch(error => {
      console.error(error)

      return ''
    })
  }, [user, internship])

  /** An empty note is a deletion on the API side, hence the message picked on the edited value. */
  const save = useCallback(value => {
    const edited = value.trim()

    setLoading(true)

    return upsertInternshipNote(user, internship, edited).then(({ data }) => {
      const saved = edited === '' || !isObject(data) ? null : data

      setNote(saved?.note ?? '')
      notify(saved === null ? [] : [saved])
      onSuccess(t(edited === '' ? 'internship_note.delete.success' : 'internship_note.upsert.success'))

      return true
    }).catch(error => {
      console.error(error)
      onError(t('internship_note.upsert.error'))

      return false
    }).finally(() => setLoading(false))
  }, [user, internship, notify, t])

  const remove = useCallback(() => {
    setLoading(true)

    return deleteInternshipNote(user, internship).then(() => {
      setNote('')
      notify([])
      onSuccess(t('internship_note.delete.success'))

      return true
    }).catch(error => {
      console.error(error)
      onError(t('internship_note.delete.error'))

      return false
    }).finally(() => setLoading(false))
  }, [user, internship, notify, t])

  return { note, loading, reload, save, remove }
}

export default useInternshipNote
